/**
 * Map index API + Redux scriptIndexList rows to fixed strip slots (SENSEX / NIFTY 50 / BANK NIFTY).
 * Do not rely on Map.values() order — keys may be id:* and server order can change.
 */

function normBlob(row) {
  return [row?.displayName, row?.script, row?.symbol, row?.name]
    .filter(Boolean)
    .join(" ")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeIndexAliasText(value) {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/**
 * Canonical benchmark index alias from raw symbol/token text.
 * Keeps matching strict to avoid routing derivative/futures ticks into benchmark slots.
 */
export function canonicalBenchmarkIndexAlias(value) {
  const s = normalizeIndexAliasText(value);
  if (!s) return "";
  if (s === "SENSEX" || s === "BSESENSEX") return "SENSEX";
  if (
    s === "NIFTY" ||
    s === "NIFTY50" ||
    s === "CNXNIFTY" ||
    s === "CNXNIFTY50"
  ) return "NIFTY";
  if (
    s === "BANKNIFTY" ||
    s === "NIFTYBANK" ||
    s === "BNIFTY" ||
    s === "CNXBANKNIFTY"
  ) return "BANKNIFTY";
  return "";
}

/** Canonical benchmark alias for an index row (from its known identity fields). */
export function rowBenchmarkIndexAlias(row) {
  const ids = indexRowIdentityTokens(row);
  for (const id of ids) {
    const a = canonicalBenchmarkIndexAlias(id);
    if (a) return a;
  }
  return "";
}

function benchmarkAliasSubscriptionTokens(canonicalAlias) {
  if (canonicalAlias === "SENSEX") {
    return ["SENSEX", "BSESENSEX", "BSE SENSEX"];
  }
  if (canonicalAlias === "NIFTY") {
    return ["NIFTY", "NIFTY50", "NIFTY 50", "CNXNIFTY", "CNX NIFTY"];
  }
  if (canonicalAlias === "BANKNIFTY") {
    return [
      "BANKNIFTY",
      "BANK NIFTY",
      "NIFTYBANK",
      "NIFTY BANK",
      "BNIFTY",
      "B NIFTY",
      "CNXBANKNIFTY",
      "CNX BANKNIFTY",
    ];
  }
  return [];
}

/** Futures/options style label — not a cash index; exclude from header benchmark slots. */
export function rowLooksLikeFoOrExpiry(row) {
  const b = normBlob(row);
  if (!b) return false;
  if (/\b(CE|PE)\b\d|\b\d{2,5}\s*(CE|PE)\b/i.test(b)) return true;
  if (/\b(FUT|FUTURE)\b/i.test(b)) return true;
  if (/\b\d{1,2}\s*[A-Z]{3}\s*\d{4}\b/.test(b)) return true;
  if (/\d{2}[A-Za-z]{3}\d{4}/.test(b.replace(/\s+/g, ""))) return true;
  return false;
}

/**
 * Choose previous close for change%: ignore absurd tick fields (volume mis-mapped as close, etc.).
 */
export function pickReasonablePrevClose(ltp, tickPrevClose, row) {
  const l = Number(ltp);
  const t = Number(tickPrevClose) || 0;
  const rowPc = Number(
    row?.previousClose ??
      row?.PreviousClose ??
      row?.prevClose ??
      row?.close ??
      row?.Close ??
      0
  );
  if (!Number.isFinite(l) || l <= 0) {
    if (Number.isFinite(t) && t > 0) return t;
    if (Number.isFinite(rowPc) && rowPc > 0) return rowPc;
    return 0;
  }
  const plausible = (p) => {
    if (!Number.isFinite(p) || p <= 0) return false;
    const ratio = p / l;
    return ratio >= 0.35 && ratio <= 4;
  };
  if (plausible(rowPc)) return rowPc;
  if (plausible(t)) return t;
  const open = Number(row?.open ?? row?.Open ?? 0);
  if (plausible(open)) return open;
  return l;
}

function takeNextUnused(rows, used) {
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    if (used.has(r)) continue;
    used.add(r);
    return r;
  }
  return null;
}

/**
 * @param {Map|unknown} scriptIndexList
 * @returns {{ sensex: object, nifty: object, bankNifty: object }}
 */
export function pickIndexStripRows(scriptIndexList) {
  const rows =
    scriptIndexList instanceof Map
      ? [...scriptIndexList.values()]
      : Array.isArray(scriptIndexList)
        ? scriptIndexList
        : [];

  const spotRows = rows.filter((row) => row && typeof row === "object" && !rowLooksLikeFoOrExpiry(row));

  let sensex = null;
  let nifty = null;
  let bankNifty = null;

  const classify = (pool) => {
    for (const row of pool) {
      if (!row || typeof row !== "object") continue;
      const b = normBlob(row);
      if (!b) continue;

      const isNifty50Spot = /^(CNX\s*)?NIFTY\s*50$|^NIFTY50$/i.test(
        b.replace(/\s+/g, " ").trim()
      );
      if (
        !bankNifty &&
        !isNifty50Spot &&
        /BANKNIFTY|BANK\s*NIFTY|NIFTY\s*BANK|CNX\s*BANKNIFTY|\bBNIFTY\b|^B\s*NIFTY/i.test(
          b
        )
      ) {
        bankNifty = row;
        continue;
      }
      if (!sensex && /\bSENSEX\b|BSE\s*SENSEX/i.test(b)) {
        sensex = row;
        continue;
      }
      if (
        !nifty &&
        /\bNIFTY\b/.test(b) &&
        !/BANK/i.test(b) &&
        !/BANKNIFTY/i.test(b) &&
        !/\bSENSEX\b/i.test(b)
      ) {
        nifty = row;
        continue;
      }
      if (
        !nifty &&
        /^(CNX\s*)?NIFTY\s*50|^NIFTY50$/i.test(b.replace(/\s+/g, " "))
      ) {
        nifty = row;
        continue;
      }
    }
  };

  classify(spotRows.length > 0 ? spotRows : rows);

  const used = new Set();
  if (sensex) used.add(sensex);
  if (nifty) used.add(nifty);
  if (bankNifty) used.add(bankNifty);

  const poolForFallback = spotRows.length > 0 ? spotRows : rows;
  const empty = {};

  return {
    sensex: sensex ?? takeNextUnused(poolForFallback, used) ?? empty,
    nifty: nifty ?? takeNextUnused(poolForFallback, used) ?? empty,
    bankNifty: bankNifty ?? takeNextUnused(poolForFallback, used) ?? empty,
  };
}

/** Join index room + subscribe (batch + per-symbol) so feeds that expect either still deliver ticks. */
export function emitIndexSocketSubscriptions(sock, scriptIndexList) {
  if (!sock || !(scriptIndexList instanceof Map) || scriptIndexList.size === 0) return;
  const tokens = indexRowsSubscriptionTokens(scriptIndexList);
  if (!tokens.length) return;
  const roomStr = tokens.toString();
  sock.emit("room", roomStr);
  sock.emit("subscribe", { symbols: tokens });
  for (let i = 0; i < tokens.length; i += 1) {
    sock.emit("subscribe", { symbol: tokens[i] });
  }
}

/** All strings / tokens the socket may use for this index row (subscribe + tick matching). */
export function indexRowIdentityTokens(row) {
  if (!row || typeof row !== "object") return [];
  const out = [];
  const seen = new Set();
  const add = (x) => {
    if (x == null || x === "") return;
    const s = String(x).trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  const keys = [
    "displayName",
    "script",
    "symbol",
    "name",
    "label",
    "Script",
    "Symbol",
    "instrumentToken",
    "instrument_token",
    "InstrumentToken",
    "token",
    "Token",
    "securityId",
    "SecurityId",
    "scriptCode",
    "bcastToken",
    "socketSymbol",
    "nseSymbol",
    "bseSymbol",
    "isin",
    "ISIN",
  ];
  for (const k of keys) add(row[k]);
  if (row.id != null && row.id !== "") add(String(row.id));
  return out;
}

/** Unique strings to join index socket room + subscribe (names + instrument tokens). */
export function indexRowsSubscriptionTokens(scriptIndexList) {
  const rows =
    scriptIndexList instanceof Map
      ? [...scriptIndexList.values()]
      : Array.isArray(scriptIndexList)
        ? scriptIndexList
        : [];

  const out = [];
  const seen = new Set();
  const pushAll = (arr) => {
    for (const s of arr) {
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
  };

  for (const v of rows) {
    if (!v || typeof v !== "object") continue;
    pushAll(indexRowIdentityTokens(v));
    const alias = rowBenchmarkIndexAlias(v);
    if (alias) pushAll(benchmarkAliasSubscriptionTokens(alias));
  }
  return out;
}

export function indexRowLtp(row) {
  if (!row || typeof row !== "object") return 0;
  const v =
    row.lastTradedPrice ??
    row.ltp ??
    row.LTP ??
    row.LastTradedPrice ??
    row.close ??
    row.Close;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Point change and % for index strip display. */
export function indexRowDisplayChange(row) {
  if (!row || typeof row !== "object") return { delta: 0, pct: 0 };
  const ltp = indexRowLtp(row);
  const tickPc = Number(
    row.PrevClose ??
      row.prevClose ??
      row.prev_close ??
      0
  );
  const pc = pickReasonablePrevClose(ltp, tickPc, row);
  const delta =
    Number.isFinite(ltp) && ltp > 0 && Number.isFinite(pc) ? ltp - pc : 0;
  const pct =
    Number.isFinite(pc) && pc !== 0 && Number.isFinite(ltp) && ltp > 0
      ? (delta / pc) * 100
      : 0;
  return { delta, pct };
}

/**
 * Display fields for index strip — matches mobile MarketWatchIndexShow / HeaderIndexStrip.
 * Prefer socket-updated changeAmount / changePercent on the row (same as mobile).
 */
export function formatIndexStripRowDisplay(row, fallbackLabel = "") {
  const ltp = indexRowLtp(row);
  const delta = Number(row?.changeAmount ?? 0);
  let chgClass = row?.changeAmountColor;
  if (!chgClass) {
    if (delta > 0) chgClass = "text-success";
    else if (delta < 0) chgClass = "text-danger";
    else chgClass = "";
  }
  return {
    label: row?.displayName || row?.script || fallbackLabel,
    ltp,
    ltpText: ltp > 0 ? ltp.toFixed(2) : "0.00",
    delta,
    deltaText: delta.toFixed(2),
    pctText: row?.changePercent ?? "0.00",
    chgClass,
    isUp: chgClass === "text-success",
    isDown: chgClass === "text-danger",
    colorLTP: row?.colorLTP,
  };
}

/** Seed change fields on index API rows — same prev-close rules as socket tick handler. */
export function seedIndexRowChangeFields(row) {
  if (!row || typeof row !== "object") return row;
  const ltp = indexRowLtp(row);
  if (!Number.isFinite(ltp) || ltp <= 0) return row;

  const pc =
    Number(row.__stablePrevClose ?? 0) > 0
      ? Number(row.__stablePrevClose)
      : pickReasonablePrevClose(ltp, 0, row);
  const chg = ltp - pc;
  const changeAmount =
    row.changeAmount != null && row.changeAmount !== ""
      ? row.changeAmount
      : chg.toFixed(2);
  const changePercent =
    row.changePercent != null && row.changePercent !== ""
      ? row.changePercent
      : pc && pc !== 0
        ? (((ltp - pc) / pc) * 100).toFixed(2)
        : "0.00";
  const changeAmountColor =
    row.changeAmountColor ??
    (chg >= 0 ? "text-success" : "text-danger");

  return {
    ...row,
    __stablePrevClose: row.__stablePrevClose ?? (pc > 0 ? pc : undefined),
    close: row.close ?? pc,
    changeAmount,
    changePercent,
    changeAmountColor,
  };
}
